import { useCallback, useMemo, useState, useRef } from 'react'
import type { FC, Dispatch, SetStateAction } from 'react'
import type { ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Temporal } from '@js-temporal/polyfill'
import {
	Box,
	Button,
	Text,
	Input,
	Heading,
	ModalContent,
	ModalBody,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	useDisclosure,
	ModalHeader,
	Grid,
	Flex,
	Divider,
	Skeleton,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverArrow,
	PopoverHeader,
	PopoverBody,
	PopoverCloseButton,
	ChakraProps,
	FormControl,
	GridItemProps,
	GridItem,
	IconButton,
	Link,
} from '@chakra-ui/react'
import type { ThemingProps } from '@chakra-ui/react'
import {
	Feature,
	SynchronizeArrowsIcn,
	PinIcn,
	ArrowLeftIcn,
} from '@alexanderathoodly/ui-library'
import {
	useForm,
	PhoneNumberField,
	EmailField,
} from '@alexanderathoodly/formify'
import { PLACEHOLDER_SRC, SIZE_290 } from '@yta/image-proxy'
import {
	CoworkingSolution,
	displayAddress,
	formatInt,
	LocationResult,
} from '@alexanderathoodly/data-models'
import type {
	PropositionResult,
	AddressResult,
	ShowingRequestPayload,
	UUID,
	ServiceOfficeMembershipResult,
	FlexOfficeResult,
} from '@alexanderathoodly/data-models'
import {
	currentUserDataQuery,
	updateUserDataMutation,
} from '~/queries/accounts'
import { getCoworkingSolutionQuery } from '~/queries/coworking_solution'
import { getLocationQuery } from '~/queries/coworking_location'
import { useAuthMutation, useAuthQuery } from '~/queries/useauthquery'
import { showingRequestsQuery } from '~/queries/showingrequest'
import { useShortlistStatusCheck } from './useShortListStatusCheck'
import { propositionQuery } from '~/queries/user_hub'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { getProviderQuery } from '../routes/coworking_location/queries'
import { useLocationPage } from '../routes/coworking_location/context'
import { ImgProxy } from '~/utils/imgproxy'
import { useSigninCheck } from 'reactfire'
import { SignInUpForm } from '~/auth/signinupform'
import {
	ShowingFormState,
	useShowingRequestForm,
} from './useshowingrequestform'
import { getGAClientId, FlexOfficeResultWithCheapestOffer } from './utils'
import { apiClient } from '~/utils/requests'
import type { User } from 'firebase/auth'
import { useCoworkingSolutionsParams } from '~/routes/coworking_location/utils'
import { Trans, useTranslation } from 'react-i18next'
import { DEFAULT_PHONE_NUMBER } from '~/settings'

function isTimeSuitable(time: Temporal.ZonedDateTime | null) {
	if (time === undefined) {
		return 'Välj en visningstid'
	}
	if (time === null) {
		return ''
	}
	const fromNow = Temporal.Now.instant().until(time.toInstant(), {
		largestUnit: 'hour',
		smallestUnit: 'hour',
	})
	if (fromNow.sign < 0 || fromNow.hours < 4) {
		return 'Visningstiden är för snart'
	}
	if (time.dayOfWeek > 5) {
		return 'Det är inte möjligt att boka visning på helgdagar'
	}
	return ''
}

const ShowingTimeField = {
	toForm: (v: Temporal.ZonedDateTime | null) => v,
	fromForm: (v: Temporal.ZonedDateTime | null) => v,
	defaultValue: undefined,
	validators: [isTimeSuitable],
}

type ShowingDateFormProps = {
	onSubmit: ({ date }: { date: Temporal.ZonedDateTime | null }) => void
	isSignedIn: boolean
}

type ShowingTimeFormProps = {
	onSubmit: ({ time }: { time: Temporal.ZonedDateTime }) => void
	isSignedIn: boolean
	selectedDay: Temporal.ZonedDateTime
}

/* 
This getFirstBookableDay returns a object of Temporal.PlainDateTime with 
next valid working day and time, which is after 2 working days from the day of 
booking. For example, if today is Monday, dayOfWeek is 1 and the returned 
object corresponds Wednesday because the next valid working day is 
Wednesday. In case the hour is after 17, the next valid working day is 
after 3 working days.
If a location does not accept calendar invitation, we send an email to the location
to notify showing requests so that they can handle the showing requests by themselves. 
Thus, we want to set 3 working days from the day of booking.
*/
export function getFirstBookableDayAndTime(
	today: Temporal.PlainDateTime,
	location: LocationResult | undefined
): Temporal.PlainDateTime {
	const currentHour = today.hour
	const todaysDayOfWeek = today.dayOfWeek
	const acceptsCalendarInvite =
		location && location.accept_calendar_showing_invitation

	const getDaysToAdd = () => {
		let daysToAdd = acceptsCalendarInvite ? 1 : 3
		const targetDay = todaysDayOfWeek + daysToAdd
		if (targetDay === 6) {
			daysToAdd += 2
		}
		if (targetDay === 7) {
			daysToAdd += 1
		}
		return daysToAdd
	}

	let firstValidDay =
		currentHour > 11 || todaysDayOfWeek > 5 || !acceptsCalendarInvite
			? today.add({ days: getDaysToAdd() })
			: today.add({ hours: 5 })

	if (currentHour >= 17 || todaysDayOfWeek >= 6) {
		// reset the hour to 13 in case the current hour is
		// after 17:00 or today is Saturday/Sunday
		firstValidDay = firstValidDay.subtract({ hours: firstValidDay.hour - 13 })
	}
	return firstValidDay
}

interface AvailableTimeCardProps {
	day: Temporal.PlainDateTime
	selectedDay: Temporal.PlainDateTime | undefined | null
}

const AvailableTimeCard: FC<GridItemProps & AvailableTimeCardProps> = ({
	day,
	selectedDay,
	...rest
}) => {
	const { i18n } = useTranslation()
	const currentLanguage = i18n.language
	const [isHovered, setIsHovered] = useState(false)
	const isSelected = useMemo(
		() => (selectedDay ? day.day == selectedDay.day : false),
		[selectedDay, day]
	)

	return (
		<Box
			as="button"
			type="button"
			bgColor={isSelected ? 'blue.500' : 'white'}
			borderRadius="5px"
			boxShadow="base"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			_hover={{ bgColor: 'blue.500' }}
			{...rest}
		>
			<Text
				fontSize="xs"
				color={isHovered || isSelected ? 'white' : 'blackAlpha.800'}
				mt="5px"
			>
				{day.toLocaleString(currentLanguage == 'sv' ? 'sv-SE' : 'en-US', {
					weekday: 'long',
					calendar: 'iso8601',
				})}
			</Text>
			<Text
				color={isHovered || isSelected ? 'white' : 'blue.500'}
				fontSize="3xl"
				fontWeight="semibold"
				lineHeight="100%"
				my="5px"
			>
				{day.day}
			</Text>
			<Text
				fontSize="xs"
				color={isHovered || isSelected ? 'white' : 'blackAlpha.800'}
				mb="5px"
			>
				{day.toLocaleString(currentLanguage == 'sv' ? 'sv-SE' : 'en-US', {
					month: 'short',
					calendar: 'iso8601',
				})}
			</Text>
		</Box>
	)
}

const ShowingDateForm: FC<ShowingDateFormProps> = ({
	onSubmit,
	isSignedIn,
}) => {
	const { t } = useTranslation()
	const { location } = useLocationPage()
	const form = useForm({
		fields: {
			date: ShowingTimeField,
		},
		onFormSubmit: onSubmit,
	})
	const [selectedDay, setSelectedDay] = useState<
		Temporal.PlainDateTime | undefined | null
	>(undefined)
	const today = Temporal.Now.plainDateTime('iso8601', 'Europe/Stockholm')
	const firstBookableDay = getFirstBookableDayAndTime(today, location)
	const bookableDays: Temporal.PlainDateTime[] = useMemo(
		() => [firstBookableDay],
		[firstBookableDay]
	)

	const maxBookableDaysLength = 24
	for (let i = 1; bookableDays.length < maxBookableDaysLength; i++) {
		const nextDay = firstBookableDay.add({ days: i })
		if (nextDay.dayOfWeek < 6) {
			bookableDays.push(nextDay)
		}
	}
	const [showFirstHalf, setShowFirstHalf] = useState(true)
	const displayedBookableDays = useMemo(() => {
		if (showFirstHalf) {
			return bookableDays.slice(0, Math.ceil(bookableDays.length / 2))
		}
		return bookableDays.slice(Math.ceil(bookableDays.length / 2))
	}, [showFirstHalf, bookableDays])

	const { data: provider } = useQuery(getProviderQuery(location?.provider))
	const buttonRef = useRef<HTMLButtonElement>(null)

	return (
		<FormControl
			as="form"
			onSubmit={(e) => {
				e.preventDefault()
				form.trySubmit()
			}}
		>
			<Text mb={5} color="blackAlpha.700">
				{t(
					'En visning tar vanligtvis 30 minuter. {{provider_name}} ger er en rundvandring av kontorshotellet och ni får möjlighet att titta på samtliga kontor som passar era behov.',
					{
						provider_name: provider?.name || t('Värden på kontorshotellet'),
					}
				)}
			</Text>
			<Text fontWeight="bold" color="blackAlpha.700" mb={5}>
				{t('Välj en dag')}
			</Text>
			<Grid templateColumns="repeat(4, 1fr)" columnGap={2.5} rowGap={2} mb={8}>
				{displayedBookableDays.map((day, index) => (
					<AvailableTimeCard
						key={`key-${index}`}
						rowSpan={1}
						colSpan={1}
						day={day}
						selectedDay={selectedDay}
						onClick={() => {
							setSelectedDay(day)
							form.setValue('date', day.toZonedDateTime('Europe/Stockholm'))
							GAAction({
								category: 'engagement',
								action: 'request_showing',
								label: 'changed_date',
							})
							sendGA4Event('initiate_showing', {
								step: 'changed_date',
							})
						}}
					/>
				))}
				<GridItem
					as={Button}
					rowSpan={1}
					colSpan={4}
					fontWeight="normal"
					whiteSpace="normal"
					color="blackAlpha.800"
					bgColor="white"
					boxShadow="base"
					_hover={{
						color: 'white',
						bgColor: 'blue.500',
					}}
					onClick={() => {
						setShowFirstHalf((prev) => !prev)
					}}
				>
					{showFirstHalf
						? t('Visa tider längre fram')
						: t('Visa tider längre bakåt')}
				</GridItem>
			</Grid>
			<Text color="red.500">
				{form.getTriedSubmit() &&
					form.getErrors('date').length > 0 &&
					form.getErrors('date')[0]}
			</Text>
			<Button type="submit" w="100%" ref={buttonRef} scrollMarginBottom={5}>
				{isSignedIn && selectedDay === null
					? t('Bekräfta bokning')
					: t('Nästa steg')}
			</Button>
			{location?.accept_calendar_showing_invitation && (
				<Text mt={5} color="blackAlpha.700">
					{t('Har du inte kalendern framför dig?')}{' '}
					<Link
						variant="link"
						fontWeight="semibold"
						color="blue.500"
						textDecoration="underline"
						onClick={() => {
							onSubmit({ date: null })
						}}
					>
						{t(
							'Anmäl intresse så kontaktar vi dig för att hitta en tid som passar'
						)}
					</Link>
				</Text>
			)}
		</FormControl>
	)
}

const ShowingTimeForm: FC<ShowingTimeFormProps> = ({
	onSubmit,
	isSignedIn,
	selectedDay,
}) => {
	const { t } = useTranslation()
	const { location } = useLocationPage()
	const form = useForm({
		fields: {
			time: ShowingTimeField,
		},
		onFormSubmit: onSubmit,
	})

	const today = Temporal.Now.plainDateTime('iso8601', 'Europe/Stockholm')
	const firstBookableDay = getFirstBookableDayAndTime(today, location)
	const bookableDays: Temporal.PlainDateTime[] = [firstBookableDay]
	const maxBookableDaysLength = location?.accept_calendar_showing_invitation
		? 6
		: 8
	for (let i = 1; bookableDays.length < maxBookableDaysLength; i++) {
		const nextDay = firstBookableDay.add({ days: i })
		if (nextDay.dayOfWeek < 6) {
			bookableDays.push(nextDay)
		}
	}

	const timeOptions = useCallback(
		(
			selectedDay: Temporal.PlainDate | null,
			firstBookableDay: Temporal.PlainDateTime
		) => {
			if (!selectedDay) {
				return []
			}
			const isFirstBookableDay = selectedDay
				? Temporal.PlainDate.compare(selectedDay, firstBookableDay) == 0
				: false
			const timeOptions: Temporal.PlainTime[] = []
			for (
				let h = isFirstBookableDay ? firstBookableDay.hour : 9;
				h < 17;
				h++
			) {
				timeOptions.push(new Temporal.PlainTime(h, 0))
				timeOptions.push(new Temporal.PlainTime(h, 30))
			}
			return timeOptions
		},
		[]
	)
	const { data: provider } = useQuery(getProviderQuery(location?.provider))
	const buttonRef = useRef<HTMLButtonElement>(null)

	return (
		<FormControl
			as="form"
			onSubmit={(e) => {
				e.preventDefault()
				form.trySubmit()
			}}
		>
			<Text mb={5} color="blackAlpha.700">
				{t(
					'En visning tar vanligtvis 30 minuter. {{provider_name}} ger er en rundvandring av kontorshotellet och ni får möjlighet att titta på samtliga kontor som passar era behov.',
					{
						provider_name: provider?.name || t('Värden på kontorshotellet'),
					}
				)}
			</Text>
			{selectedDay && (
				<>
					<Text fontWeight="bold" color="blackAlpha.700" mb={5}>
						{t('Välj en tid')}
					</Text>
					<Grid
						templateColumns="repeat(auto-fill, minmax(70px, 1fr))"
						columnGap={1.5}
						rowGap={2}
						mb={8}
						mt={4}
					>
						{timeOptions(selectedDay.toPlainDate(), firstBookableDay).map(
							(time) => {
								const isSelected =
									selectedDay.day == form.getValue('time')?.day &&
									time.hour == form.getValue('time')?.hour &&
									time.minute == form.getValue('time')?.minute
								return (
									<Button
										key={time.toLocaleString('sv-SE')}
										bgColor={isSelected ? 'blue.500' : 'blue.100'}
										color={isSelected ? 'white' : ' black.800'}
										borderRadius="full"
										size="sm"
										py={2}
										px={4}
										w="max-content"
										h={10}
										justifySelf="center"
										fontWeight="semibold"
										_hover={{
											color: 'white',
											bgColor: 'blue.500',
										}}
										onClick={() => {
											form.setValue(
												'time',
												time.toZonedDateTime({
													plainDate: selectedDay.toPlainDate(),
													timeZone: 'Europe/Stockholm',
												})
											)
											GAAction({
												category: 'engagement',
												action: 'request_showing',
												label: 'changed_time',
											})
											sendGA4Event('initiate_showing', {
												step: 'changed_time',
											})
										}}
									>
										{time.toLocaleString('sv-SE', {
											hour: '2-digit',
											minute: '2-digit',
											calendar: 'iso8601',
										})}
									</Button>
								)
							}
						)}
					</Grid>
				</>
			)}
			<Text color="red.500">
				{form.getTriedSubmit() &&
					form.getErrors('time').length > 0 &&
					form.getErrors('time')[0]}
			</Text>
			<Button
				type="submit"
				w="100%"
				onClick={() =>
					GAAction({
						category: 'engagement',
						action: 'request_showing',
						label: 'time_slot_selected',
					})
				}
				ref={buttonRef}
				scrollMarginBottom={5}
			>
				{isSignedIn ? t('Bekräfta bokning') : t('Nästa steg')}
			</Button>
		</FormControl>
	)
}

interface ShowingTimeAndPlaceProps {
	time: string
	place: string
}

const ShowingTimeAndPlaceBase: FC<ChakraProps & ShowingTimeAndPlaceProps> = ({
	time,
	place,
	...rest
}) => {
	const { t } = useTranslation()
	return (
		<Box {...rest}>
			<Text fontWeight="bold" color="blackAlpha.700" mb={2}>
				{t('Tid för visning')}
			</Text>
			<Text color="blackAlpha.700" mb={4}>
				{time}
			</Text>
			<Text fontWeight="bold" color="blackAlpha.700" mb={2}>
				{t('Plats')}
			</Text>
			<Text color="blackAlpha.700">{place}</Text>
		</Box>
	)
}

const LocationShowingTimeAndPlace: FC<
	ChakraProps & {
		location: LocationResult
		time: string
	}
> = ({ location, time, ...rest }) => {
	return (
		<ShowingTimeAndPlaceBase
			time={time}
			place={displayAddress(location.address)}
			{...rest}
		/>
	)
}

const SolutionOrMembershipShowingTimeAndPlace: FC<
	ChakraProps & {
		locationId: string
		time: string
	}
> = ({ locationId, time, ...rest }) => {
	const queryClient = useQueryClient()
	const locationResult = useQuery(getLocationQuery(locationId, queryClient))
	if (locationResult.isIdle || locationResult.isLoading) {
		return <div>Loading...</div>
	}
	if (locationResult.isError) {
		return <div>Error!</div>
	}
	const location = locationResult.data
	return (
		<ShowingTimeAndPlaceBase
			time={time}
			place={displayAddress(location.address)}
			{...rest}
		/>
	)
}

const PropositionShowingTimeAndPlace: FC<
	ChakraProps & {
		proposition: PropositionResult
		time: string
	}
> = ({ proposition, time, ...rest }) => {
	return (
		<ShowingTimeAndPlaceBase
			time={time}
			place={displayAddress(proposition.address)}
			{...rest}
		/>
	)
}

const ShowingTimeAndPlace: FC<
	ChakraProps & {
		showingType: ShowingTypes
		time: Temporal.ZonedDateTime | null
	}
> = ({ showingType, time, ...rest }) => {
	const { t, i18n } = useTranslation()
	const currentLanguage = i18n.language
	const timeToBeDisplayed = time
		? `${time.toLocaleString(currentLanguage == 'sv' ? 'sv-SE' : 'en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				timeZone: 'Europe/Stockholm',
		  })} - ${time
				.add({ minutes: 30 })
				.toLocaleString(currentLanguage == 'sv' ? 'sv-SE' : 'en-US', {
					hour: '2-digit',
					minute: '2-digit',
					timeZone: 'Europe/Stockholm',
				})}`
		: t('Vi kontaktar dig för att hitta en lämplig tid.')
	return 'location' in showingType ? (
		<LocationShowingTimeAndPlace
			location={showingType.location}
			time={timeToBeDisplayed}
			{...rest}
		/>
	) : 'solution' in showingType ? (
		<SolutionOrMembershipShowingTimeAndPlace
			locationId={showingType.solution.location}
			time={timeToBeDisplayed}
			{...rest}
		/>
	) : 'membership' in showingType ? (
		<SolutionOrMembershipShowingTimeAndPlace
			locationId={showingType.membership.location}
			time={timeToBeDisplayed}
			{...rest}
		/>
	) : 'flexoffice' in showingType ? (
		<SolutionOrMembershipShowingTimeAndPlace
			locationId={showingType.flexoffice.location}
			time={timeToBeDisplayed}
			{...rest}
		/>
	) : (
		<PropositionShowingTimeAndPlace
			proposition={showingType.proposition}
			time={timeToBeDisplayed}
			{...rest}
		/>
	)
}

type UserInfoFormProps = {
	isPhoneNumberInputStep: boolean
	onSubmit: (data: { phone_number: string; email: string }) => void
	showingType: ShowingTypes
	showing_time: Temporal.ZonedDateTime | null
	showSignUpForm?: () => void
}

const UserInfoForm: FC<UserInfoFormProps> = ({
	isPhoneNumberInputStep,
	onSubmit,
	showingType,
	showing_time,
	showSignUpForm,
}) => {
	const { t } = useTranslation()
	const form = useForm({
		fields: {
			phone_number: PhoneNumberField(
				localStorage.getItem('phoneNumber')
					? {
							required: true,
							defaultValue: localStorage.getItem('phoneNumber')!,
					  }
					: { required: true }
			),
			email: EmailField(
				localStorage.getItem('phoneNumber')
					? {
							required: isPhoneNumberInputStep ? false : true,
							defaultValue: localStorage.getItem('email')!,
					  }
					: { required: isPhoneNumberInputStep ? false : true }
			),
		},
		onFormSubmit: onSubmit,
	})

	return (
		<FormControl
			as="form"
			onSubmit={(e) => {
				e.preventDefault()
				form.trySubmit()
			}}
		>
			<Text fontSize="sm" color="grey.500" fontWeight="normal" mb={5}>
				{t(
					'För att bekräfta din visningsbokning och skicka ut en visningsinbjudan behöver vi era kontaktuppgifter. När visningen är bekräftad kommer vi skicka en kallelse till din mail.'
				)}
			</Text>
			<Text color="grey.500" fontSize="sm">
				{t('Mobilnummer')}
			</Text>
			<Input
				type="tel"
				name="phone_number"
				placeholder={t('Mobiltelefonnummer')}
				onChange={(e) => form.setValue('phone_number', e.target.value)}
				value={form.getValue('phone_number')}
			></Input>
			{!isPhoneNumberInputStep && (
				<>
					<Text color="red.500">
						{form.getTriedSubmit() &&
							form.getErrors('phone_number').length > 0 &&
							form.getErrors('phone_number')[0]}
					</Text>
					<Text color="grey.500" fontSize="sm" mt={2}>
						{t('E-post')}
					</Text>
					<Input
						type="email"
						name="email"
						placeholder={t('E-post')}
						onChange={(e) => form.setValue('email', e.target.value)}
						value={form.getValue('email')}
					></Input>
					<Text color="red.500">
						{form.getTriedSubmit() &&
							form.getErrors('email').length > 0 &&
							form.getErrors('email')[0]}
					</Text>
				</>
			)}
			<ShowingTimeAndPlace
				time={showing_time}
				showingType={showingType}
				mt={8}
			/>
			<Divider my={8} />
			{showSignUpForm && (
				<Text mb={8}>
					<Trans
						i18nKey="Har du redan ett konto? Logga in här."
						defaults="Har du redan ett konto? <button>Logga in här.</button>"
						components={{
							button: (
								<Button
									variant="link"
									onClick={showSignUpForm}
									fontSize="sm"
									textDecoration="underline"
								/>
							),
						}}
					/>
				</Text>
			)}
			<Button w="full" type="submit">
				{t('Bekräfta bokning')}
			</Button>
			<Text mt={2}>
				{t(
					'Är du på språng och vill få till en visning direkt? Ring oss på {{phone_number}} så stämmer vi av om någon är på plats för att ta emot dig.',
					{ phone_number: DEFAULT_PHONE_NUMBER }
				)}
			</Text>
		</FormControl>
	)
}

const LocationSummary: FC<{
	location: LocationResult
}> = ({ location }) => {
	const { t } = useTranslation()
	const { data: provider } = useQuery(getProviderQuery(location.provider))
	return (
		<>
			<Heading
				as="h2"
				color="blackAlpha.700"
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="bold"
				mb={5}
			>
				{t('Sammanställning')}
			</Heading>
			<Flex columnGap="15px" alignItems="start">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={location.images[0] || PLACEHOLDER_SRC}
					width={{ base: 24, md: 32, lg: 40 }}
					alt="locaton summary image"
				/>
				<Box>
					{provider && (
						<Text
							fontWeight="semibold"
							color="blackAlpha.800"
							fontSize={{ base: 'md', md: 'lg' }}
							mt={{ base: 0, md: 2 }}
						>
							{provider.name}
						</Text>
					)}
					<Text fontSize={{ base: 'sm', md: 'md' }} color="blackAlpha.700">
						{displayAddress(location.address)}
						<PinIcn
							h={{ base: 3, md: 4 }}
							w={{ base: 3, md: 4 }}
							outer={{
								fill: 'blue.500',
								stroke: 'blue.500',
							}}
							inner={{ fill: 'white' }}
							display="inline-flex"
							ml={1}
						/>
					</Text>
				</Box>
			</Flex>
		</>
	)
}

const SolutionSummary: FC<{
	solutionId: string
}> = ({ solutionId }) => {
	const queryClient = useQueryClient()
	const solutionResult = useQuery(
		getCoworkingSolutionQuery(solutionId, queryClient)
	)
	const locationResult = useQuery(
		getLocationQuery(solutionResult.data?.location, queryClient)
	)
	if (
		solutionResult.isLoading ||
		solutionResult.isIdle ||
		locationResult.isIdle ||
		locationResult.isLoading
	) {
		return <div>Loading...</div>
	}
	if (solutionResult.isError || locationResult.isError) {
		return <div>Error!</div>
	}
	const location = locationResult.data
	const solution = solutionResult.data
	const solutionData = {
		workspaces: solution.workspaces,
		size: solution.size,
		rent: solution.price,
		image: { src: location.images[0], alt: `${location.name} bild 1` },
		thumbnail: solution.thumbnail,
	}

	return (
		<BookingSummary
			solution={solutionData}
			address={location.address}
			name={location.name}
		/>
	)
}

const PropositionSummary: FC<{
	propositionId: string
}> = ({ propositionId }) => {
	const propositionResult = useAuthQuery(propositionQuery, propositionId)

	if (propositionResult.isLoading || propositionResult.isIdle) {
		return <div>Loading...</div>
	}
	if (propositionResult.isError) {
		return <div>Error!</div>
	}

	const proposition = propositionResult.data
	const solution = {
		workspaces: proposition.workspaces,
		size: proposition.size,
		rent: proposition.offer.monthly_rent,
		image: { src: proposition.images[0], alt: `${proposition.title} bild 1` },
		thumbnail: null,
	}
	return (
		<BookingSummary
			address={proposition.address}
			solution={solution}
			name={proposition.title}
		/>
	)
}

const MembershipSummary: FC<{
	membership: ServiceOfficeMembershipResult
}> = ({ membership }) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const locationResult = useQuery(
		getLocationQuery(membership.location, queryClient)
	)
	const providerResult = useQuery(
		getProviderQuery(locationResult.data?.provider)
	)
	if (
		locationResult.isIdle ||
		locationResult.isLoading ||
		providerResult.isIdle ||
		providerResult.isLoading
	) {
		return <div>Loading...</div>
	}
	if (locationResult.isError || providerResult.isError) {
		return <div>Error!</div>
	}
	const provider = providerResult.data
	const location = locationResult.data

	return (
		<>
			<Heading
				as="h2"
				color="blackAlpha.700"
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="bold"
				mb={5}
			>
				{t('Sammanställning')}
			</Heading>

			<Flex columnGap="15px" alignItems="start">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={location.images[0] || PLACEHOLDER_SRC}
					width={{ base: 24, md: 32, lg: 40 }}
					alt="locaton summary image"
				/>
				<Box>
					{provider && (
						<Text
							fontWeight="semibold"
							color="blackAlpha.800"
							fontSize={{ base: 'md', md: 'lg' }}
							mt={{ base: 0, md: 2 }}
						>
							{provider.name}
						</Text>
					)}
					<Text fontSize={{ base: 'sm', md: 'md' }} color="blackAlpha.700">
						{displayAddress(location.address)}
						<PinIcn
							h={{ base: 3, md: 4 }}
							w={{ base: 3, md: 4 }}
							outer={{
								fill: 'blue.500',
								stroke: 'blue.500',
							}}
							inner={{ fill: 'white' }}
							display="inline-flex"
							ml={1}
						/>
					</Text>
					<Text>
						{t('Medlemskap')}: {membership.name}
					</Text>
				</Box>
			</Flex>
		</>
	)
}

const FlexOfficeSummary: FC<{
	flexOffice: FlexOfficeResultWithCheapestOffer
}> = ({ flexOffice }) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const locationResult = useQuery(
		getLocationQuery(flexOffice.location, queryClient)
	)
	const providerResult = useQuery(
		getProviderQuery(locationResult.data?.provider)
	)
	if (
		locationResult.isIdle ||
		locationResult.isLoading ||
		providerResult.isIdle ||
		providerResult.isLoading
	) {
		return <div>Loading...</div>
	}
	if (locationResult.isError || providerResult.isError) {
		return <div>Error!</div>
	}
	const location = locationResult.data

	return (
		<>
			<Heading
				as="h2"
				color="blackAlpha.700"
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="bold"
				mb={5}
			>
				{t('Sammanställning')}
			</Heading>
			<Flex columnGap="15px" alignItems="start">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={flexOffice.images[0] || PLACEHOLDER_SRC}
					w={{ base: 24, md: 32, lg: 40 }}
					alt="Eget kontor"
				/>
				<Flex wrap="wrap" columnGap="8px" rowGap="4px" alignItems="center">
					<Text
						fontWeight="semibold"
						color="blackAlpha.800"
						fontSize={{ base: 'md', md: 'lg' }}
						mt={{ base: 0, md: 2 }}
					>
						{location.name}
					</Text>
					<Text fontSize={{ base: 'sm', md: 'md' }} color="blackAlpha.700">
						{displayAddress(location.address)}
						<PinIcn
							h={{ base: 3, md: 4 }}
							w={{ base: 3, md: 4 }}
							outer={{
								fill: 'blue.500',
								stroke: 'blue.500',
							}}
							inner={{ fill: 'white' }}
							display="inline-flex"
							ml={1}
						/>
					</Text>
				</Flex>
			</Flex>
			<Flex mt={5} wrap="wrap" columnGap="8px" rowGap="4px" alignItems="center">
				<Text w={{ base: 'auto', md: '100%' }}>{t('Lösningsförslag')} :</Text>
				<Feature
					name={t('Plats', { count: 2 })}
					value={
						flexOffice.max_workspaces
							? flexOffice.max_workspaces.toString()
							: ''
					}
				/>
				<Feature
					name="m²"
					value={
						flexOffice.size
							? `${formatInt(flexOffice.size)}m²`
							: t('Ingen information')
					}
				/>
				<Feature
					name={t('Kostnad')}
					value={
						flexOffice.fromPrice
							? `${t('Från')} ${formatInt(flexOffice.fromPrice)} ${t('kr/mån')}`
							: t('Ingen information')
					}
				/>
			</Flex>
		</>
	)
}

const BookingSummary: FC<{
	solution: {
		workspaces: number | null
		size: number | null
		rent: number | null
		image: { src: string | undefined; alt: string }
		thumbnail: string | null
	}
	name: string
	address: AddressResult
}> = ({ solution, name, address }) => {
	const { t } = useTranslation()
	return (
		<>
			<Heading
				as="h2"
				color="blackAlpha.700"
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="bold"
				mb={5}
			>
				{t('Sammanställning')}
			</Heading>
			<Flex columnGap="15px" alignItems="start">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={solution.image.src || PLACEHOLDER_SRC}
					w={{ base: 24, md: 32, lg: 40 }}
					alt={solution.image.alt}
				/>
				<Flex wrap="wrap" columnGap="8px" rowGap="4px" alignItems="center">
					<Text
						fontWeight="semibold"
						color="blackAlpha.800"
						fontSize={{ base: 'md', md: 'lg' }}
						mt={{ base: 0, md: 2 }}
					>
						{name}
					</Text>
					<Text fontSize={{ base: 'sm', md: 'md' }} color="blackAlpha.700">
						{displayAddress(address)}
						<PinIcn
							h={{ base: 3, md: 4 }}
							w={{ base: 3, md: 4 }}
							outer={{
								fill: 'blue.500',
								stroke: 'blue.500',
							}}
							inner={{ fill: 'white' }}
							display="inline-flex"
							ml={1}
						/>
					</Text>
				</Flex>
			</Flex>
			<Flex mt={5} columnGap="15px" alignItems="start">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={solution.thumbnail || PLACEHOLDER_SRC}
					loading="lazy"
					width={{ base: 24, md: 32, lg: 40 }}
					alt={
						solution.thumbnail
							? `Planritning för ${name}${
									solution.workspaces
										? `, ${solution.workspaces} arbetsplatser`
										: ''
							  }${solution.size ? `, ${solution.size} m²` : ''}`
							: `ingen tillgänglig bild`
					}
					flex={1}
				/>
				<Flex wrap="wrap" columnGap="8px" rowGap="4px" alignItems="center">
					<Text w={{ base: 'auto', md: '100%' }}>{t('Lösningsförslag')} :</Text>
					<Feature
						name={t('Plats', { count: 2 })}
						value={solution.workspaces ? solution.workspaces.toString() : ''}
					/>
					<Feature
						name={t('Arbetsrum')}
						value={
							solution.workspaces
								? solution.workspaces.toString()
								: t('Ingen information')
						}
					/>
					<Feature
						name="m²"
						value={
							solution.size
								? `${formatInt(solution.size)}m²`
								: t('Ingen information')
						}
					/>
					<Feature
						name={t('pris/mån')}
						value={
							solution.rent
								? `${formatInt(solution.rent)} ${t('kr/mån')}`
								: t('Ingen information')
						}
					/>
				</Flex>
			</Flex>
		</>
	)
}

function getLocationId(props: ShowingTypes) {
	return 'location' in props
		? props.location.id
		: 'solution' in props
		? props.solution.location
		: 'membership' in props
		? props.membership.location
		: undefined
}

function useGA4EventForShowingRequest(props: ShowingTypes) {
	const locationId = getLocationId(props)
	const queryClient = useQueryClient()
	const location = useQuery(getLocationQuery(locationId, queryClient))
	const provider = useQuery(getProviderQuery(location.data?.provider))
	const searchParams = useCoworkingSolutionsParams()
	const getValue = () => {
		if ('solution' in props) {
			return (props.solution.price * 12) / 50
		}
		if ('membership' in props) {
			return (props.membership.price * 12) / 50
		}
		if ('proposition' in props && props.proposition.original_monthly_rent) {
			return (props.proposition.original_monthly_rent * 12) / 50
		}
		if (searchParams.budget) {
			return (searchParams.budget * 12) / 50
		}
		return 10000
	}

	return () =>
		sendGA4Event('request_showing', {
			category: 'lead',
			budget: searchParams.budget || 0,
			size: searchParams.workspaces,
			city: location.data?.breadcrumbs.city.name,
			value: getValue(),
			currency: 'sek',
			operator: provider.data?.name || 'failed_to_fetch_provider',
			location: location.data?.name || 'failed_to_fetch_location',
		})
}

type ShowingTypes =
	| { solution: CoworkingSolution }
	| { proposition: PropositionResult }
	| { location: LocationResult; workspaces?: number }
	| { membership: ServiceOfficeMembershipResult }
	| { flexoffice: FlexOfficeResultWithCheapestOffer }

type ShowingFormProps = ShowingTypes & {
	closeShowingForm?: () => void
	goToNextStep: (steps?: number) => void
	state: ShowingFormState
	setState: Dispatch<SetStateAction<ShowingFormState>>
	isSignUpFormShown: boolean
	showSignUpForm: () => void
}

type ShowingRequestMutation = Omit<ShowingRequestPayload, 'ga_client_id'> &
	(
		| {
				proposition: UUID
		  }
		| {
				solution: UUID
		  }
		| {
				location: UUID
				workspaces: number | undefined
		  }
		| { membership: UUID }
		| { flex_office: UUID }
	)

export const ShowingForm: FC<ShowingFormProps> = ({
	goToNextStep,
	state,
	setState,
	isSignUpFormShown,
	showSignUpForm,
	...props
}) => {
	const { t } = useTranslation()
	const { status, data: signInCheckResult } = useSigninCheck()
	const currentUser = useAuthQuery(currentUserDataQuery)
	const updateUserMutation = useAuthMutation(updateUserDataMutation)
	const queryClient = useQueryClient()
	const showingRequestMutation = useMutation({
		mutationFn: async (data: ShowingRequestMutation) => {
			const gaClientId = await getGAClientId()
			return await apiClient.requestShowing({
				...data,
				ga_client_id: gaClientId,
			})
		},
		onSuccess: () => {
			void queryClient.invalidateQueries(['showing_requests'])
			void queryClient.invalidateQueries(['shortlist', 'current'])
		},
	})
	const GA4EventSender = useGA4EventForShowingRequest(props)

	const onSubmitConfirmation = useCallback(
		async (
			showing_time: Temporal.ZonedDateTime | null,
			email: string,
			phone_number: string
		) => {
			if ('location' in props) {
				await showingRequestMutation.mutateAsync({
					showing_time:
						showing_time === null
							? showing_time
							: showing_time.toString({
									calendarName: 'never',
									timeZoneName: 'never',
							  }),
					location: props.location.id,
					workspaces: props.workspaces,
					email,
					phone_number,
				})
				GA4EventSender()
			} else if ('solution' in props) {
				await showingRequestMutation.mutateAsync({
					showing_time:
						showing_time === null
							? showing_time
							: showing_time.toString({
									calendarName: 'never',
									timeZoneName: 'never',
							  }),
					solution: props.solution.id,
					email,
					phone_number,
				})
				GA4EventSender()
			} else if ('membership' in props) {
				await showingRequestMutation.mutateAsync({
					showing_time:
						showing_time === null
							? showing_time
							: showing_time.toString({
									calendarName: 'never',
									timeZoneName: 'never',
							  }),
					membership: props.membership.id,
					email,
					phone_number,
				})
				GA4EventSender()
			} else if ('flexoffice' in props) {
				await showingRequestMutation.mutateAsync({
					showing_time:
						showing_time === null
							? showing_time
							: showing_time.toString({
									calendarName: 'never',
									timeZoneName: 'never',
							  }),
					flex_office: props.flexoffice.id,
					email,
					phone_number,
				})
				GA4EventSender()
			} else {
				await showingRequestMutation.mutateAsync({
					showing_time:
						showing_time === null
							? showing_time
							: showing_time.toString({
									calendarName: 'never',
									timeZoneName: 'never',
							  }),
					proposition: props.proposition.id,
					email,
					phone_number,
				})
			}
			GAAction({
				category: 'engagement',
				action: 'request_showing',
				label: 'request_sent',
				metrics: {
					metric2:
						'solution' in props
							? props['solution'].workspaces
							: 'workspaces' in props
							? props['workspaces']
							: undefined,
				},
			})

			setState({
				step: 'success',
				showing_time: showing_time,
			})
			goToNextStep()
		},
		[showingRequestMutation, props, setState, goToNextStep, GA4EventSender]
	)

	const onSubmitTime = useCallback(
		({ time }: { time: Temporal.ZonedDateTime | null }) => {
			if (currentUser.isError) {
				throw new Error(currentUser.error.message)
			}
			if (
				status !== 'loading' &&
				signInCheckResult &&
				signInCheckResult.signedIn &&
				currentUser.data
			) {
				if (!currentUser.data.phone_number) {
					setState({ ...state, showing_time: time, step: 'phone_number_input' })
					goToNextStep()
					return
				}
				void onSubmitConfirmation(
					time,
					currentUser.data.email,
					currentUser.data.phone_number
				)
			} else {
				setState({ step: 'contact', showing_time: time })
				goToNextStep()
				sendGA4Event('initiate_showing', { step: 'confirm_time' })
			}
		},
		[
			onSubmitConfirmation,
			signInCheckResult,
			status,
			currentUser,
			setState,
			goToNextStep,
			state,
		]
	)

	const onSubmitDate = useCallback(
		({ date }: { date: Temporal.ZonedDateTime | null }) => {
			if (date != null) {
				setState({ step: 'time', selectedDay: date })
				goToNextStep()
				sendGA4Event('initiate_showing', { step: 'confirm_date' })
				return
			}

			if (
				status !== 'loading' &&
				signInCheckResult &&
				signInCheckResult.signedIn &&
				currentUser.data
			) {
				setState({
					showing_time: date,
					step: 'phone_number_input',
				})
				goToNextStep()
			} else {
				setState({ showing_time: date, step: 'contact' })
				goToNextStep(2)
			}
			sendGA4Event('initiate_showing', { step: 'confirm_time' })
		},
		[setState, goToNextStep, signInCheckResult, status, currentUser]
	)

	const onSubmitContact = useCallback(
		async ({
			email,
			phone_number,
		}: {
			phone_number: string
			email: string
		}) => {
			if (!('showing_time' in state)) {
				throw new Error('No showing time selected')
			}
			if (state.step == 'phone_number_input') {
				await updateUserMutation.mutateAsync({ phone_number })
			}
			void onSubmitConfirmation(
				state.showing_time,
				currentUser.data?.email || email,
				phone_number
			)
		},
		[state, updateUserMutation, onSubmitConfirmation, currentUser.data]
	)

	const onClickSignUpForm = useCallback(
		({ user }: { user: User }) => {
			if (state.step != 'contact') {
				return
			}
			if (!user.email) {
				throw new Error('User does not have his/her email.')
			}
			const email = user.email
			// Without setTimeout here, useAuthMutation for currentUser does not detect that user has logged in
			setTimeout(() => {
				const phoneNumber = currentUser.data?.phone_number || user.phoneNumber
				if (!phoneNumber) {
					setState({ ...state, step: 'phone_number_input' })
					goToNextStep()
					return
				}
				return void onSubmitConfirmation(state.showing_time, email, phoneNumber)
			}, 1000)
		},
		[currentUser, onSubmitConfirmation, state, setState, goToNextStep]
	)

	if (state.step === 'date') {
		return (
			<ShowingDateForm
				onSubmit={onSubmitDate}
				isSignedIn={signInCheckResult ? signInCheckResult.signedIn : false}
			/>
		)
	}
	if (state.step === 'time' && state.selectedDay) {
		return (
			<ShowingTimeForm
				onSubmit={onSubmitTime}
				isSignedIn={signInCheckResult ? signInCheckResult.signedIn : false}
				selectedDay={state.selectedDay}
			/>
		)
	}
	if (state.step === 'contact') {
		if (isSignUpFormShown) {
			return <SignInUpForm defaultType="signIn" onClick={onClickSignUpForm} />
		}
		return (
			<UserInfoForm
				isPhoneNumberInputStep={false}
				onSubmit={onSubmitContact}
				showingType={props}
				showing_time={state.showing_time}
				showSignUpForm={showSignUpForm}
			/>
		)
	}
	if (state.step == 'phone_number_input') {
		return (
			<UserInfoForm
				isPhoneNumberInputStep={true}
				onSubmit={onSubmitContact}
				showingType={props}
				showing_time={state.showing_time}
			/>
		)
	} else if (state.step === 'success') {
		return (
			<Box>
				<Text color="grey.500" mb={5}>
					{t(
						'Behöver du ändra tid för visningen vid ett senare tillfälle? Ingen fara, ta kontakt med oss så ordnar vi det. På visningen får du möjlighet att se samtliga kontor som är relevanta för ert team på anläggningen.'
					)}
				</Text>
				<ShowingTimeAndPlace
					time={state.showing_time}
					showingType={props}
					mb={8}
				/>
				{'location' in props ? (
					<LocationSummary location={props.location} />
				) : 'solution' in props ? (
					<SolutionSummary solutionId={props.solution.id} />
				) : 'membership' in props ? (
					<MembershipSummary membership={props.membership} />
				) : 'flexoffice' in props ? (
					<FlexOfficeSummary flexOffice={props.flexoffice} />
				) : (
					<PropositionSummary propositionId={props.proposition.id} />
				)}
			</Box>
		)
	}
	return <></>
}

export type BookShowingModalContentProps = ShowingTypes & {
	closeShowingForm?: () => void
}

export const BookShowingModalContent: FC<BookShowingModalContentProps> = ({
	closeShowingForm,
	...propsForShowingForm
}) => {
	const { t } = useTranslation()
	const { status, data: signInCheckResult } = useSigninCheck()
	const {
		state,
		setState,
		currentStep,
		lastStep,
		goToNextStep,
		goToPreviousStep,
		isSignUpFormShown,
		showSignUpForm,
	} = useShowingRequestForm(status, signInCheckResult)

	return (
		<ModalContent>
			{state.step == 'contact' && (
				<IconButton
					h="28px"
					minW="28px"
					maxW="28px"
					position="absolute"
					top={3}
					left={6}
					variant="outline"
					isRound
					aria-label="Go back"
					icon={
						<ArrowLeftIcn
							h={3}
							w="20px"
							strokeWidth={4}
							sx={{ path: { stroke: 'blue.500' } }}
						/>
					}
					onClick={goToPreviousStep}
				/>
			)}
			<ModalCloseButton />
			<ModalHeader mt={8} pb={3}>
				<Skeleton
					isLoaded={lastStep != undefined}
					w="full"
					display="flex"
					justifyContent="space-between"
					alignItems="end"
				>
					<Text
						fontWeight="semibold"
						color="blackAlpha.800"
						fontSize={{ base: 'lg', md: '2xl' }}
					>
						{currentStep == lastStep
							? t('Din bokning är klar')
							: t('Boka visning')}
					</Text>
					<Text fontSize="xs" color="blackAlpha.700">{`${currentStep}/${
						lastStep as number // ESLint apparently does not detect isLoaded in Skelton
					}`}</Text>
				</Skeleton>
			</ModalHeader>
			<Divider w="90%" mx="auto" bgColor="blackAlpha.200" />
			<ModalBody pt={5} pb={10}>
				{closeShowingForm ? (
					<ShowingForm
						{...propsForShowingForm}
						closeShowingForm={closeShowingForm}
						goToNextStep={goToNextStep}
						state={state}
						setState={setState}
						isSignUpFormShown={isSignUpFormShown}
						showSignUpForm={showSignUpForm}
					/>
				) : (
					<ShowingForm
						{...propsForShowingForm}
						goToNextStep={goToNextStep}
						state={state}
						setState={setState}
						isSignUpFormShown={isSignUpFormShown}
						showSignUpForm={showSignUpForm}
					/>
				)}
			</ModalBody>
		</ModalContent>
	)
}

type ShowingButtonProps = (
	| { solution: CoworkingSolution }
	| { proposition: PropositionResult }
	| { location: LocationResult; workspaces?: number | undefined }
	| { membership: ServiceOfficeMembershipResult }
	| { flexoffice: FlexOfficeResult }
) & {
	customButton?: ReactElement | null
} & ChakraProps &
	ThemingProps

export const RequestShowingButton: FC<ShowingButtonProps> = (props) => {
	const { t } = useTranslation()
	const { onOpen, onClose, isOpen } = useDisclosure()
	const { propsForShowingForm } = useShortlistStatusCheck(props)
	const { customButton } = props
	return (
		<>
			{customButton ? (
				customButton
			) : (
				<Button
					onClick={() => {
						onOpen()
						GAAction({
							category: 'engagement',
							action: 'request_showing',
							label: 'request_initiated',
							metrics: {
								metric2:
									'solution' in props
										? props['solution'].workspaces
										: 'workspaces' in props
										? props['workspaces']
										: undefined,
							},
						})
						sendGA4Event('initiate_showing', {
							step: 'initiate',
							location:
								'solution' in props
									? 'solution list'
									: 'propostion' in props
									? 'shortlist'
									: 'location' in props
									? 'conversion box'
									: 'membership' in props
									? 'membership'
									: 'flexoffice' in props
									? 'flex office'
									: 'no location value',
						})
					}}
					width={{ base: '100%', lg: 'auto' }}
					{...props}
				>
					{t('Önska visning')}
				</Button>
			)}
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size={{ base: 'full', md: 'md' }}
			>
				<ModalOverlay />
				<BookShowingModalContent
					{...propsForShowingForm}
					closeShowingForm={onClose}
				/>
			</Modal>
		</>
	)
}

/**
 * A button that can be used to either show the current showing scheduled for the proposition, the showing request, or otherwise let the user book a showing
 * for the proposition.
 */
export const PropositionShowingButton: FC<
	{
		proposition: PropositionResult
	} & ChakraProps
> = ({ proposition, ...rest }) => {
	const { t, i18n } = useTranslation()
	const currentLanguage = i18n.language
	const nextShowingTime = useMemo(
		() =>
			proposition.next_showing === null
				? null
				: Temporal.Instant.from(
						proposition.next_showing.start
				  ).toZonedDateTimeISO('Europe/Stockholm'),
		[proposition.next_showing]
	)
	const showingRequestsResult = useAuthQuery(showingRequestsQuery)
	if (showingRequestsResult.isLoading || showingRequestsResult.isIdle) {
		return <Skeleton h={10} />
	}
	if (showingRequestsResult.isError) {
		return <div>Error: {showingRequestsResult.error.message}</div>
	}
	const request = showingRequestsResult.data.find(
		(r) => r.proposition === proposition.id
	)
	if (nextShowingTime !== null) {
		return (
			<Popover isLazy placement="top">
				<PopoverTrigger>
					<Button colorScheme="green" {...rest}>
						{t('Visning')}{' '}
						{nextShowingTime.toLocaleString(
							currentLanguage == 'sv' ? 'sv-SE' : 'en-US',
							{
								month: '2-digit',
								day: '2-digit',
							}
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent>
					<PopoverHeader>{t('Visning')}</PopoverHeader>
					<PopoverArrow />
					<PopoverCloseButton />
					<PopoverBody>
						{t('Du har en bokad visning')}{' '}
						{nextShowingTime.toLocaleString(
							currentLanguage == 'sv' ? 'sv-SE' : 'en-US',
							{
								dateStyle: 'full',
								timeStyle: 'short',
							}
						)}
						. {t('Kontakta din expert om du vill om- eller avboka.')}
					</PopoverBody>
				</PopoverContent>
			</Popover>
		)
	}

	const customButton =
		request && request.status === 'pending' ? (
			<Button
				disabled
				variant="outline"
				leftIcon={<SynchronizeArrowsIcn h={5} w={5} strokeWidth={1.5} />}
				_disabled={{ opacity: '1', cursor: 'not-allowed' }}
				color="grey.800"
				colorScheme="grey"
				{...rest}
			>
				{t('Behandlas')}
			</Button>
		) : request?.status == 'refused' || proposition.archived ? (
			<Button
				disabled
				variant="outline"
				_disabled={{
					opacity: '1',
					cursor: 'not-allowed',
				}}
				_hover={{
					bgColor: 'red.400',
				}}
				color="white"
				bgColor="red.400"
				{...rest}
			>
				{t('Rejected')}
			</Button>
		) : null

	return (
		<RequestShowingButton
			proposition={proposition}
			customButton={customButton}
			{...rest}
		/>
	)
}
